import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import {
  Company,
  CompanyAccountView,
  ICompanyAccountView,
  IUserCompanyAccess,
  User,
  UserAccessPermissionsRequest
} from 'src/app/services/api/api-model';
import { DataService } from 'src/app/services/api/data.service';
import { environment } from 'src/environments/environment';
import { StaticDataService } from '../../services/api/static-data.service';
import { ModalService } from '../../services/modal.service';
import { SessionService } from '../../services/session/session.service';
import { AccountantServiceBase } from '../accountant-service-base';

@Injectable({
  providedIn: 'root'
})
export class AccountantCompaniesService extends AccountantServiceBase implements OnDestroy {
  public get sourceCompanies(): Observable<ICompanyAccountView[]> {
    return this.sourceCompaniesSubject.asObservable();
  }

  public get companies(): Observable<ICompanyAccountView[]> {
    if (!this.companiesSubject || !this.allCompanies || (this.allCompanies && this.allCompanies.length === 0)) {
      this.loadData();
    }
    return this.companiesSubject.asObservable();
  }

  protected ngUnsubscribe: Subject<{}> = new Subject();
  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public clearCompanySubject(): void {
    this.selectedId = null;
    this.currentSelectId = null;
    this.companySelected = null;

    this.allCompanies = [];
    this.companiesSubject.next([]);
    this.rolesSubject.next([]);
  }

  public isDirty = false;
  public showSaveDataConfirmationChangeState = false;
  public navigationToStateName: string;
  public company: ICompanyAccountView = new CompanyAccountView();

  public EditRoleList: UserAccessPermissionsRequest[] = [];
  public EditCompanyList: Company[] = [];

  public readloadUserConpanyRolChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  public companySelected: any;

  private companiesSubject: BehaviorSubject<ICompanyAccountView[]> = new BehaviorSubject<ICompanyAccountView[]>([]);
  private allCompanies: ICompanyAccountView[] = [];
  private sourceCompaniesSubject: BehaviorSubject<ICompanyAccountView[]> = new BehaviorSubject<ICompanyAccountView[]>(
    []
  );
  private currentSelectId: number;

  private originalComment: string;

  constructor(
    protected api: DataService,
    protected sessionService: SessionService,
    protected modalService: ModalService,
    protected staticDataService: StaticDataService
  ) {
    super(api, modalService, sessionService, staticDataService);
  }

  public loadData(isClearSelect = true): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.api
      .Account_GetAccountCompanies()
      .pipe(tap((d: ICompanyAccountView[]) => d.forEach((rec: any) => (rec.GoToIcon = 'sign-in'))))
      .subscribe((data: ICompanyAccountView[]) => {
        this.allCompanies = data;
        this.sourceCompaniesSubject.next(
          data.map((c: ICompanyAccountView) => {
            return { ...c, Name: `${c.Name} [${c.Id}]` };
          })
        );
        this.isLoading = false;

        this.applyFilter(isClearSelect);
      });
  }

  public save(dataRoleList: UserAccessPermissionsRequest[]): void {
    this.company.AccountComment = this.company.AccountComment || null;
    if (this.company && this.originalComment !== this.company.AccountComment) {
      this.api
        .Account_SetAccountComment(this.selectedId, { Comment: this.company.AccountComment })
        .subscribe(() => (this.originalComment = this.company.AccountComment));
    }
    super.save(dataRoleList);
  }

  public discard(): void {
    this.company.AccountComment = this.originalComment;
    super.discard();
  }

  protected getRoles(): void {
    this.readloadUserConpanyRolChange.emit(true);
    this.getRoleServicePublic();
  }

  public getRoleServicePublic(): void {
    if (this.selectedId && this.selectedId !== this.currentSelectId) {
      this.currentSelectId = this.selectedId;
      this.rolesSubject.next([]);

      this.company = this.companiesSubject.value.find((c: ICompanyAccountView) => c.Id === this.selectedId);
      this.originalComment = this.company ? this.company.AccountComment : undefined;
      this.api.Account_GetUserCompanyAccess(this.selectedId).subscribe((data: IUserCompanyAccess[]) => {
        if (data && data.length > 0) {
          if (data[0].CompanyId === this.selectedId) {
            this.rolesSubject.next(data);
          } else {
            if (!environment.production) {
              console.error(`CompanyId ${this.selectedId} doesnt match recieved data`);
            }
          }
        } else {
          if (!environment.production) {
            console.log('No roles');
          }
        }
      });
    }
  }

  protected applyFilter(isClearSelect = true): void {
    if (this.filter) {
      this.currentPage = 0;
      const searchText: string = this.filter.toLocaleLowerCase();
      const data: ICompanyAccountView[] = this.allCompanies.filter(
        (c: ICompanyAccountView) =>
          (c.VATRegistrationNumber && c.VATRegistrationNumber.toLocaleLowerCase().includes(searchText)) ||
          c.Name.toLocaleLowerCase().includes(searchText) ||
          c.Id.toString().includes(searchText) ||
          (c.MainContactFullName && c.MainContactFullName.toLocaleLowerCase().includes(searchText)) ||
          (c.InvoiceEmail && c.InvoiceEmail.toLocaleLowerCase().includes(searchText)) ||
          (c.AccountComment && c.AccountComment.toLocaleLowerCase().includes(searchText))
      );

      this.companiesSubject.next(data);
    } else {
      this.companiesSubject.next(this.allCompanies);
    }

    if (isClearSelect) {
      this.selectedId = undefined;
    }
  }

  protected updateData(model: User[]): void {}

  protected reloadData(): void {}
}
